import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { APIService } from 'src/app/services/api.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { getENV, getRaceType } from 'src/app/_helpers/helpers';
import {
  BASE_STATION_STEPS,
  BaseStationStep,
  CalibrationResponse,
  CalibrationStatus,
} from './base-station-data';
import { VenueDetailsService } from 'src/app/modules/globals/dash/modules/settings/services/venue-details.service';

@Component({
  selector: 'base-station-calibration',
  templateUrl: './base-station-calibration.component.html',
  styleUrls: ['./base-station-calibration.component.css'],
})
export class BaseStationCalibrationComponent implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() eventId: string;
  @Input() venueId: string;
  @Input({ required: true }) venueName: string;
  @Input({ required: true }) kit: string;
  @Input() operator: string;
  @Output() hideModel = new EventEmitter<boolean>();


  selectedKit: string = '';

  steps: BaseStationStep[] = JSON.parse(JSON.stringify(BASE_STATION_STEPS));
  loading: boolean = false;
  calibrationInProgress: boolean = false;
  destroyed: boolean = false;
  currentStep: number = 0;
  failedCount: number = 0;
  stepData: any = {};
  interval: any = null;
  statusCheckInterval: any = null;
  hasCalibrationData: boolean = false;
  calibrationData: any = null;
  event: any = null;
  operatorName: string = '';
  lastFailedStep: number = -1;
  hasFailedSteps: boolean = false;
  selectedStation: any = null;
  waitingForStationSelection: boolean = false;
  showRouterIPPopup: boolean = false;
  routerIPs: { ip: string; name: string }[] = [];
  selectedRouterIP: string = '';

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private venueDetailsService: VenueDetailsService
  ) {}

  ngOnInit() {
    this.checkInitialStatus();
    this.getKits();
  }

  kits: any[] = [];
  async getKits(): Promise<boolean> {
    return new Promise(async (resolve: any) => {
      try {
        const apiURL = `${this.config[getENV()].raceAPI}/flask-operations`;
        const payload = {
          action: 'get_kits',
        };
        const header: any = {
          'X-Api-Key': 'e2bvuAoslY1VJvFMqhr5x7nxoEhFVRii9jRngS3U',
        };

        const result: any = await this.apiService.postDataPromis(apiURL, payload, header);

        if (result.kits) {
          const kitNames: string[] = result.kits.map((kit: any) => kit.name);
          this.kits = kitNames.map(name => ({ value: name }));
        }

        // After loading kits, fetch venue details to get default transceiver kit
        await this.loadVenueDetailsAndSetDefaultKit();

        resolve(true);
      } catch (error) {
        console.error('Error loading kits:', error);
        resolve(false);
      }
    });
  }

  private async loadVenueDetailsAndSetDefaultKit(): Promise<void> {
    try {
      const response = await this.venueDetailsService.getLocationDetails(
        this.config,
        this.venueId,
        getRaceType(this.venueId)['raceTypeStr'],
        this.venueName
      );

      if (response?.venues) {
        const defaultKit = response.venues.DefaultTransceiverKit;
        if (defaultKit && this.kits.some(k => k.value === defaultKit)) {
          this.kit = defaultKit;
          this.selectedKit = defaultKit;
        } else if (this.kits.length > 0) {
          this.kit = this.kits[0].value;
          this.selectedKit = this.kit;
        }
      }
    } catch (error) {
      console.error('Error loading venue details:', error);
    }
  }


  async checkInitialStatus() {
    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      step.status = 'STARTED';
      await this.checkCalibrationStatus(true, step.index);
      this.steps[i] = step;
      if (step.status === ('FAILED' as CalibrationStatus)) {
        break;
      } else if (
        step.status === ('IN_PROGRESS' as CalibrationStatus) ||
        step.status === ('STARTED' as CalibrationStatus)
      ) {
        this.startCalibration(i, false);
        break;
      } else if (step.status == ('PENDING' as CalibrationStatus)) {
        break;
      } else {
        step.status = 'COMPLETED';
      }
    }

    this.calibrationInProgress = false;
  }

  async startCalibration(fromStep: number = 0, reload: boolean = true) {
    if (fromStep === 0 && reload) {
      // Reset all steps when starting fresh
      this.steps = JSON.parse(JSON.stringify(BASE_STATION_STEPS));
    }

    this.calibrationInProgress = true;
    this.currentStep = fromStep;
    this.failedCount = 0;
    this.hasFailedSteps = false;

    // this.startStatusCheck();

    for (let i = fromStep; i < this.steps.length; i++) {
      if (this.destroyed) break;

      this.currentStep = i;
      const step = this.steps[i];
      step.comment = ''; // Clear previous comments

      if (i === 7) {
        this.waitingForStationSelection = true;
        break; // Stop the process until the user selects a station
      }

      await this.executeSingleStep(i + 1, step);
      if (step.status === 'FAILED') {
        this.lastFailedStep = i;
        this.hasFailedSteps = true;
        this.calibrationInProgress = false;
        break;
      }
    }

    this.stopStatusCheck();
    this.calibrationInProgress = false;
    this.loading = false;
  }

  mapData: any = null;
  openMapPopup(stations: any) {
    this.mapData = stations;
  }

  handleCloseMap() {
    this.mapData = null;
  }

  private startStatusCheck() {
    this.statusCheckInterval = setInterval(() => {
      this.checkCalibrationStatus(false);
    }, 10000);
  }

  private stopStatusCheck() {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
  }

  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  private async executeSingleStep(stepNumber: number, step: BaseStationStep) {
    try {
      let result: any;
      if (step.status == 'IN_PROGRESS' || step.status == 'STARTED') {
        result = { status: '1', message: 'In Progress' };
      } else {
        step.status = 'STARTED';
        result = await this.executeStep(stepNumber);
      }

      if (result.status === '1') {
        step.status = 'IN_PROGRESS';
        step.comment = result.message;

        let currentStep = this.steps[stepNumber - 1];

        // Parse and store any additional data
        if (result.response?.Comments) {
          const comments = result.response?.Comments;
          try {
            if (typeof comments === 'string') {
              try {
                let parsedComments: any = JSON.parse(comments);
                if (stepNumber == 10) {
                  let data = {};
                  Object.keys(parsedComments['data']).forEach((key) => {
                    data[key.toLowerCase()] = parsedComments['data'][key];
                    delete parsedComments['data'][key];
                  });
                  parsedComments = {
                    ...parsedComments,
                    ...data,
                  };
                  delete parsedComments['data'];
                }

                if (stepNumber == 7) {
                  const stations = parsedComments['nearest_sations'].stations
                    .map((station: any) => {
                      return `${station['mountpoint']} - ${station['distance']} km`;
                    })
                    .join(', ');
                  parsedComments['stations'] = stations;
                  currentStep['nearest_sations'] =
                    parsedComments['nearest_sations'];
                  delete parsedComments['nearest_sations'];
                }

                step.displayData = parsedComments;
              } catch (e) {
                // Keep as string if not valid JSON
              }
            }
          } catch (e) {}
        }

        if (stepNumber === 7 && step?.['nearest_sations']?.['stations']) {
          // Handle the dropdown for step 8
          this.waitingForStationSelection = true;
          return; // Stop the process until the user selects a station
        } else {
          await this.waitForStepCompletion(stepNumber);
        }
      } else {
        step.status = 'FAILED';
        step.comment = result.message;
        this.failedCount++;
      }
    } catch (error) {
      step.status = 'FAILED';
      step.comment = error.message;
      this.failedCount++;
    }
  }

  private async executeParallelSteps() {
    const step8 = this.steps[7];

    // Wait for station selection before starting step 8
    if (this.waitingForStationSelection) {
      return;
    }

    this.calibrationInProgress = true;

    // Start step 8
    await this.executeSingleStep(8, step8);
    if (step8.status === 'FAILED') {
      this.calibrationInProgress = false;
      return;
    }

    // While step 8 is running, execute 9 and 10 sequentially
    const step9 = this.steps[8];
    const step10 = this.steps[9];

    await this.executeSingleStep(9, step9);

    if (step9.status === 'FAILED') {
      this.calibrationInProgress = false;
      step8['status'] = 'COMPLETED';
      step8['displayData'] = { message: 'Forward RTCM Data completed' };
      return;
    }

    await this.executeSingleStep(10, step10);

    if (step10.status === 'FAILED') {
      this.calibrationInProgress = false;
      step8['status'] = 'COMPLETED';
      step8['displayData'] = { message: 'Forward RTCM Data completed' };
      return;
    }

    // Wait for step 8 to complete
    step8['status'] = 'COMPLETED';
    step8['displayData'] = { message: 'Forward RTCM Data completed' };
    this.startCalibration(10);
  }

  submitStationSelection() {
    if (this.selectedStation) {
      this.steps[6].displayData[
        'selectedStation'
      ] = `${this.selectedStation.mountpoint} - ${this.selectedStation.distance} km`;
      this.waitingForStationSelection = false;
      this.executeParallelSteps();
    }
  }

  private async waitForStepCompletion(stepNumber: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const interval = setInterval(async () => {
        try {
          const status = await this.checkCalibrationStatus(false, stepNumber);
          if (stepNumber == 8 && status == 'IN_PROGRESS') {
            resolve();
          }
          if (status === 'COMPLETED' || status === 'FAILED' || this.destroyed) {
            clearInterval(interval);
            this.steps[stepNumber - 1].status = status as CalibrationStatus;
            resolve();
          }
        } catch (error) {
          clearInterval(interval);
          this.steps[stepNumber - 1].status = 'FAILED';
          resolve();
        }
      }, 10000); // Ensure the interval is set to 10 seconds
    });
  }

  private async executeStep(stepNumber: number): Promise<any> {
    const apiURL = `${this.config[getENV()].raceAPI}/flask-operations`;
    let payload = {
      action: 'bs_calibration',
      event_id: this.eventId,
      venue_id: this.venueId,
      step: stepNumber,
      operator_name: this.operator,
      router_ip: this.selectedRouterIP,
    };

    // Changed: Check previous step's data (stepNumber-2 because arrays are 0-based)
    const previousStep = stepNumber > 1 ? this.steps[stepNumber - 2] : null;
    if (previousStep?.displayData) {
      Object.assign(payload, previousStep.displayData);
    }

    if (stepNumber == 12) {
      Object.assign(payload, this.steps[9].displayData);
    }

    // Also include current step data if it exists
    if (this.steps[stepNumber - 1]?.displayData) {
      Object.assign(payload, this.steps[stepNumber - 1].displayData);
    }

    // Include selected station mountpoint for step 8
    if (stepNumber === 8 && this.selectedStation) {
      payload['mountpoint'] = this.selectedStation.mountpoint;
    }

    return await this.apiService.postDataPromis(apiURL, payload);
  }

  private parseCalibrationData(comments: string): any {
    if (!comments) return null;
    try {
      const data = JSON.parse(comments);
      // Remove status:1 from display
      if (data.status) delete data.status;
      return data;
    } catch (e) {
      return null;
    }
  }

  async checkCalibrationStatus(
    isInitialLoad: boolean = false,
    stepNumber?: number
  ): Promise<CalibrationStatus> {
    if (this.destroyed) return 'FAILED';

    const apiURL = `${this.config[getENV()].raceAPI}/flask-operations`;
    const payload = {
      action: 'action_status',
      button_action: 'bs_calibration',
      event_id: this.eventId,
      report_action: 'bs_calibration',
      venue_id: this.venueId,
      operator_name: this.operator,
      step_number: stepNumber,
      step: stepNumber,
    };

    const currentStep = this.steps[stepNumber - 1];

    if (
      currentStep['status'] == 'COMPLETED' ||
      currentStep['status'] == 'FAILED'
    ) {
      return currentStep['status'];
    }

    try {
      const result: CalibrationResponse = await this.apiService.postDataPromis(
        apiURL,
        payload
      );

      if (result.status === '1' || result.s === '1') {
        result.response.StepCompletionStatus =
          result.response?.StepCompletionStatus || 'PENDING';

        if (result.response?.StepCompletionStatus === 'PENDING') {
          return 'PENDING';
        }
        const status =
          result.response?.StepCompletionStatus?.toUpperCase().replace(
            ' ',
            '_'
          ) as CalibrationStatus;
        let comments = result.response?.Comments;

        // Handle comments parsing
        if (typeof comments === 'string') {
          try {
            let parsedComments: any = JSON.parse(comments);
            if (stepNumber == 10) {
              let data = {};
              Object.keys(parsedComments['data']).forEach((key) => {
                data[key.toLowerCase()] = parsedComments['data'][key];
                delete parsedComments['data'][key];
              });
              parsedComments = {
                ...parsedComments,
                ...data,
              };
              delete parsedComments['data'];
            }

            if (stepNumber == 7) {
              const stations = parsedComments['nearest_sations'].stations
                .map((station: any) => {
                  return `${station['mountpoint']} - ${station['distance']} km`;
                })
                .join(', ');
              parsedComments['stations'] = stations;
              currentStep['nearest_sations'] =
                parsedComments['nearest_sations'];
              delete parsedComments['nearest_sations'];
            }

            comments = parsedComments;
          } catch (e) {
            // Keep as string if not valid JSON
          }
        }

        // Handle individual step updates
        if (stepNumber && !isInitialLoad) {
          currentStep.comment = typeof comments === 'string' ? comments : '';
          currentStep.displayData =
            typeof comments === 'object' ? comments : null;

          if (status === 'COMPLETED') {
            this.notifier.alert(
              'Success',
              '',
              `Step ${stepNumber}: ${currentStep.name} completed`,
              'success',
              3000
            );
          }
        }

        // Handle calibration data
        const calibrationData = this.parseCalibrationData(
          result.response?.Comments
        );
        this.hasCalibrationData = !!calibrationData;
        if (this.event) {
          this.event.bs_calibration = this.hasCalibrationData;
        }

        // Handle initial load
        if (isInitialLoad) {
          this.calibrationData = calibrationData;
          this.loading = false;
          if (status === 'STARTED' || status === 'IN_PROGRESS') {
            this.calibrationInProgress = true;
            setTimeout(
              () => this.checkCalibrationStatus(false, stepNumber),
              10000
            );
          }
          return status;
        }

        // Handle status updates
        switch (status) {
          case 'COMPLETED':
            if (!stepNumber) {
              this.notifier.alert(
                'Success',
                '',
                'Base Station Calibration completed successfully',
                'success',
                5000
              );
              this.calibrationInProgress = false;
              this.calibrationData = calibrationData;
            }
            break;

          case 'FAILED':
            this.notifier.alert(
              'Error',
              '',
              result.response?.Comments || 'Calibration step failed',
              'error',
              5000
            );
            this.calibrationInProgress = false;
            break;

          case 'STARTED':
          case 'IN_PROGRESS':
            // if (!this.destroyed) {
            //   setTimeout(() => this.checkCalibrationStatus(false, stepNumber), 10000);
            // }
            break;
        }

        return status;
      }

      throw new Error(
        result.error_message || 'Failed to check calibration status'
      );
    } catch (error) {
      this.notifier.alert(
        'Error',
        '',
        error.message || 'Failed to check calibration status',
        'error',
        5000
      );
      this.calibrationInProgress = false;
      return 'FAILED';
    } finally {
      this.loading = false;
    }
  }

  rerunFromFailed() {
    if (this.lastFailedStep >= 0) {
      this.startCalibration(this.lastFailedStep);
    }
  }

  close() {
    this.hideModel.emit(false);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.stopStatusCheck();
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  routerIPPrefix: string = '';
  async openRouterIPPopup() {
    this.notifier.loading(true);
    try {
      const type: string = getRaceType(this.venueId)['raceTypeStr'];
      const response = await this.venueDetailsService.getLocationDetails(
        this.config,
        this.venueId,
        type,
        this.venueName
      );
      if (response && response.venues) {
        const octets = response.venues?.RouterIP?.split('.') || [];
        if (octets.length < 3) {
          this.notifier.alert('Error', '', 'Router IP is not configured properly', 'error', 5000);
          return;
        } else {
          this.routerIPPrefix = octets.slice(0, 3).join('.');
        }
        if (this.selectedKit) {
          this.getRouterIPs();
        }
        if (!this.routerIPPrefix) {
          this.notifier.alert(
            'Error',
            '',
            'Router IP not found',
            'error',
            5000
          );
        }
      }
    } catch (error) {
      this.notifier.alert(
        'Error',
        '',
        'Failed to load router IP details',
        'error',
        5000
      );
    }
    this.notifier.loading(false);
    this.showRouterIPPopup = true;
  }

  async getRouterIPs() {
    this.notifier.loading(true);
    const kitDetails = await this.venueDetailsService.getKitDetails(
      this.config,
      this.selectedKit
    );

    if (kitDetails.kits && kitDetails.kits.length > 0) {
      const kit = kitDetails.kits[0];
      const ltsConnections = kit.lts.map((connector: any) => ({
        connectionName: connector.connector_name,
        connectionType: connector.connector_type,
        ipAddress: connector.ip_address,
        port: connector.port,
      }));

      this.routerIPs = ltsConnections.map((connection) => {
        const lastOctet = connection.ipAddress.split('.').pop();
        return {
          ip: `${this.routerIPPrefix}.${lastOctet}`,
          name: `${this.routerIPPrefix}.${lastOctet} (${connection.connectionName})`,
        };
      });
    }

    this.notifier.loading(false);
  }

  submitRouterIPSelection() {
    if (this.selectedRouterIP) {
      this.showRouterIPPopup = false;
      this.calibrationInProgress = true;
      this.startCalibration();
    } else {
      this.notifier.alert(
        'Error',
        '',
        'Please select a valid router IP',
        'error',
        5000
      );
    }
  }
}
